import { useLayoutEffect, useRef, useState } from 'react';

export interface UniformHeightTarget {
    /** 目标选择器 */
    selector: string;
    /** 分组ID，相同groupId的元素会统一高度 */
    groupId?: string;
    /** 初始最小高度，避免CLS */
    minHeight?: number;
}

export interface UniformHeightOptions {
    /** 目标选择器，用于指定需要统一高度的子元素 */
    selector?: string;
    /** 多目标配置，支持同时统一多个元素的高度 */
    targets?: UniformHeightTarget[];
    /** 分组ID，相同groupId的元素会统一高度 */
    groupId?: string;
    /** 初始最小高度，避免CLS */
    minHeight?: number;
    /** 防抖延迟时间，毫秒 */
    debounceDelay?: number;
    /** 是否启用，可用于条件性控制 */
    enabled?: boolean;
    /** 刷新触发器，当此值变化时会重新计算高度 */
    refreshTrigger?: any;
}

/**
 * 统一高度Hook
 * 自动检测并统一一组元素的高度，避免CLS布局偏移
 */
export const useUniformHeight = (options: UniformHeightOptions = {}) => {
    const { selector = '[data-uniform-target]', targets, groupId = 'default', minHeight = 40, debounceDelay = 100, enabled = true, refreshTrigger } = options;

    const containerRef = useRef<HTMLElement>(null);
    const [maxHeights, setMaxHeights] = useState<Record<string, number>>({});
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 生成目标配置列表
    const getTargetConfigs = (): UniformHeightTarget[] => {
        if (targets && targets.length > 0) {
            return targets.map((target) => ({
                groupId: target.groupId || groupId,
                minHeight: target.minHeight || minHeight,
                ...target,
            }));
        }
        // 兼容原有的单选择器模式
        return [{ selector, groupId, minHeight }];
    };

    const updateHeights = () => {
        if (!containerRef.current || !enabled) return;

        const targetConfigs = getTargetConfigs();
        const newMaxHeights: Record<string, number> = {};

        targetConfigs.forEach((config) => {
            const elements = containerRef.current!.querySelectorAll(config.selector) as NodeListOf<HTMLElement>;
            if (elements.length === 0) return;

            // 临时清除CSS变量，获取自然高度
            elements.forEach((el) => {
                el.style.removeProperty('--uniform-height');
            });

            // 计算最大高度
            let max = config.minHeight || minHeight;
            elements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                if (rect.height > max) {
                    max = rect.height;
                }
            });

            // 设置统一高度
            const targetKey = `${config.groupId}-${config.selector}`;
            newMaxHeights[targetKey] = max;
            elements.forEach((el) => {
                el.style.setProperty('--uniform-height', `${max}px`);
                // // 同时设置带前缀的CSS变量，便于特定目标样式
                el.style.setProperty(`--uniform-height-${config.groupId}`, `${max}px`);
            });
        });

        setMaxHeights(newMaxHeights);
    };

    const debouncedUpdateHeights = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(updateHeights, debounceDelay);
    };

    useLayoutEffect(() => {
        if (!enabled || !containerRef.current) return;

        // 初始计算
        updateHeights();

        // 设置ResizeObserver
        resizeObserverRef.current = new ResizeObserver(debouncedUpdateHeights);

        // 设置MutationObserver监听DOM内容变化
        mutationObserverRef.current = new MutationObserver(debouncedUpdateHeights);
        mutationObserverRef.current.observe(containerRef.current, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: false,
        });

        const targetConfigs = getTargetConfigs();
        targetConfigs.forEach((config) => {
            const elements = containerRef.current!.querySelectorAll(config.selector);
            elements.forEach((el) => {
                resizeObserverRef.current?.observe(el as Element);
            });
        });

        // 监听窗口大小变化
        window.addEventListener('resize', debouncedUpdateHeights);

        return () => {
            // 清理
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            if (mutationObserverRef.current) {
                mutationObserverRef.current.disconnect();
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            window.removeEventListener('resize', debouncedUpdateHeights);
        };
    }, [JSON.stringify(targets), selector, groupId, enabled, debounceDelay, refreshTrigger]);

    return {
        containerRef,
        maxHeights,
        // 兼容旧API，返回第一个目标的高度
        maxHeight: Object.values(maxHeights)[0] || minHeight,
        refresh: updateHeights,
    };
};
